import { isCredentialBearingPath } from '../credential-bearing-path';
import { publishConsentDefault } from './consent-mode-bootstrap';
import { getOrCreateDataLayer } from './data-layer';

/**
 * Loads the Google Tag Manager container that consumes the dataLayer feed
 * in `gtm-data-layer-adapter.ts`.
 *
 * Without this, `rio.track` / `rio.page` / `rio.identify` entries queue in
 * `window.dataLayer` and nothing reads them: the container's ad pixel tags
 * are triggered off those entries, so the client half of the client/server
 * conversion dedup (PX-270) only exists once a container is on the page.
 *
 * ## The URL is our own Google Tag Gateway, and must be absolute
 *
 * {@link GTM_GATEWAY_BASE_URL} proxies googletagmanager.com through our
 * apex domain, which keeps the container script on a first-party host and
 * out of reach of ad blockers that filter on Google's. It has to be the
 * absolute `https://www.respondent.io` form even though this app is also
 * respondent.io: the gateway lives on the marketing origin, and the same
 * `/mu35/` path on the app origin resolves to app HTML.
 *
 * The `id` query parameter is not decorative — the gateway 404s an unknown
 * container, and answers a *missing* id with its own default container. So
 * the id always goes on the URL explicitly rather than being left off and
 * inherited.
 *
 * ## Production only
 *
 * Same rule as the legacy app (`cookie-consent.hbs`, which loads this same
 * container behind an `isProduction` check): one container, configured
 * against production ad accounts, so loading it anywhere else would send
 * staging and QA traffic to live pixels. GTM Environments would be the way
 * to give non-production its own container state; that is deliberately not
 * built here.
 *
 * ## Consent
 *
 * The container loads regardless of consent, because Consent Mode withholds
 * *tags*, not the container: a loaded container under denied consent fires
 * nothing its per-tag consent checks cover, which is the mechanism the whole
 * design leans on.
 *
 * What those checks must have is consent state to read, so this function
 * publishes the default itself (`publishConsentDefault`, the single writer)
 * before injecting the script, rather than depending on the GA adapter
 * having been constructed first. GA is configuration away from not existing
 * on this page at all, and a container with no consent state fails in both
 * directions at once — the ad pixels' Additional Consent Checks fail closed
 * and never fire, while Google's own built-in Consent Mode tags fail open
 * and fire unconsented.
 */

/**
 * Our Google Tag Gateway measurement path. The container script is
 * `gtm.js` beneath it, mirroring the googletagmanager.com layout the
 * gateway stands in for.
 */
const GTM_GATEWAY_BASE_URL = 'https://www.respondent.io/mu35';

/** The one environment whose traffic belongs on the live container. */
const GTM_CONTAINER_ENVIRONMENT = 'production';

/**
 * Injects the container script, and returns whether the page now has it
 * (or already did). Idempotent. Publishes the consent default in any
 * browser; injects nothing on a credential-bearing path, outside
 * production, without a container id, or during SSR.
 */
export function loadGtmContainer(env: {
  GOOGLE_TAG_MANAGER_CONTAINER_ID: string;
  ENVIRONMENT: string;
}): boolean {
  if (globalThis.document === undefined) return false;

  // Ahead of every branch below, including the one that finds a container
  // already on the page: that container is already evaluating tags, and a
  // default published late still beats one never published at all.
  publishConsentDefault();

  if (isCredentialBearingPath(globalThis.location.pathname)) return false;
  if (!env.GOOGLE_TAG_MANAGER_CONTAINER_ID) return false;
  if (env.ENVIRONMENT !== GTM_CONTAINER_ENVIRONMENT) return false;

  const src = containerScriptUrl(env.GOOGLE_TAG_MANAGER_CONTAINER_ID);

  // Idempotency is a lookup for the script we would add rather than a
  // module-scoped "already ran" flag, because it has to hold against a
  // container this module didn't inject: this app is served under a path
  // prefix of the same site as the marketing pages, so a shared shell or a
  // server-rendered snippet could get there first, and two copies of one
  // container double-fire every tag in it. The url is fully encoded, so
  // the interpolation below cannot break out of the attribute selector.
  if (document.querySelector(`script[src="${src}"]`) !== null) return true;

  // The canonical snippet's start entry, pushed before the script so the
  // container reads its own load timing off the dataLayer.
  getOrCreateDataLayer().push({ 'gtm.start': Date.now(), event: 'gtm.js' });

  const script = document.createElement('script');
  script.async = true;
  script.src = src;
  document.head.append(script);

  return true;
}

function containerScriptUrl(containerId: string): string {
  return `${GTM_GATEWAY_BASE_URL}/gtm.js?id=${encodeURIComponent(containerId)}`;
}

/**
 * Test-only reset hook — takes the container script back off the page and
 * clears the dataLayer the loader pushes onto, so each test starts from a
 * clean page.
 */
export function __resetGtmContainerLoaderForTesting() {
  globalThis.dataLayer = undefined;
  if (globalThis.document === undefined) return;
  for (const script of document.querySelectorAll(
    `script[src^="${GTM_GATEWAY_BASE_URL}"]`,
  )) {
    script.remove();
  }
}
